import React, { useState } from 'react';
import ReactPlayer from 'react-player';
import ReactMarkdown from 'react-markdown';
import {
  ViewableMultiSubmission,
  findArtifactByExtensions,
  getStringManifestFieldValue,
  isValidHttpUrl,
  normalizeHttpUrl,
  projectLabels,
  toEmbeddableVideoUrl,
  trackLabelFor,
} from './submissionMedia';
import '../../pages/Evaluations.css';

interface SectionProps {
  submission: ViewableMultiSubmission;
  competitionId: number;
}

export function projectVideoLink(submission: ViewableMultiSubmission): string {
  return normalizeHttpUrl(getStringManifestFieldValue(submission.manifest, 'video_link'));
}

export const ProjectOverviewSection: React.FC<SectionProps> = ({ submission, competitionId }) => {
  const labels = projectLabels(competitionId);
  const manifest = submission.manifest;
  const submissionTitle = getStringManifestFieldValue(manifest, 'submission_title');
  const videoLink = projectVideoLink(submission);
  const agentLink = normalizeHttpUrl(getStringManifestFieldValue(manifest, 'agent_link'));
  const rawTrack = getStringManifestFieldValue(manifest, 'track');
  const trackLabel = trackLabelFor(competitionId, rawTrack || submission.metadata_track);
  const teamLabel = (
    (submission.metadata_team || '').trim()
    || submission.team_name
    || 'Individual submission'
  );

  return (
    <div className="evaluations-multi-section">
      <h3>Project Title</h3>
      <div className="evaluations-multi-item">
        <div className="evaluations-multi-item__value">{submissionTitle || 'Title not provided.'}</div>
      </div>
      <div className="evaluations-multi-item">
        <div className="evaluations-multi-item__label">Team</div>
        <div className="evaluations-multi-item__value">{teamLabel}</div>
      </div>
      <div className="evaluations-comp14-links evaluations-comp14-links--prominent">
        {videoLink ? (
          <a href={videoLink} target="_blank" rel="noopener noreferrer" className="evaluations-comp14-link-card">
            <span aria-hidden="true">🎬</span>
            <span>Open video link</span>
          </a>
        ) : (
          <div className="evaluations-comp14-link-card evaluations-comp14-link-card--muted">
            <span aria-hidden="true">🎬</span>
            <span>Video link not provided.</span>
          </div>
        )}
        {labels.showAgentLink && (agentLink ? (
          <a href={agentLink} target="_blank" rel="noopener noreferrer" className="evaluations-comp14-link-card">
            <span aria-hidden="true">🤖</span>
            <span>Open agent link</span>
          </a>
        ) : (
          <div className="evaluations-comp14-link-card evaluations-comp14-link-card--muted">
            <span aria-hidden="true">🤖</span>
            <span>Agent link not provided.</span>
          </div>
        ))}
      </div>
      <div className="evaluations-multi-item">
        <div className="evaluations-multi-item__label">Track</div>
        <div className="evaluations-multi-item__value">{trackLabel}</div>
      </div>
    </div>
  );
};

export const ProjectVideoSection: React.FC<SectionProps> = ({ submission, competitionId }) => {
  const [playbackError, setPlaybackError] = useState(false);
  const videoLink = projectVideoLink(submission);
  const hasVideoLink = isValidHttpUrl(videoLink);
  const canReactPlayerHandleVideo = hasVideoLink ? (ReactPlayer.canPlay?.(videoLink) ?? false) : false;
  const embeddedVideoUrl = hasVideoLink ? toEmbeddableVideoUrl(videoLink) : '';
  const useIframeFallback = hasVideoLink && (!canReactPlayerHandleVideo || playbackError);

  return (
    <div className="evaluations-multi-section">
      <h3>Video Preview</h3>
      {hasVideoLink ? (
        <div className="evaluations-video-wrapper evaluations-comp14-video-wrapper">
          {useIframeFallback ? (
            <iframe
              src={embeddedVideoUrl}
              title={`Competition ${competitionId} video`}
              className="evaluations-comp14-video-iframe"
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <ReactPlayer
              src={videoLink}
              controls
              width="100%"
              height="100%"
              className="evaluations-video-player"
              onError={() => setPlaybackError(true)}
            />
          )}
        </div>
      ) : (
        <p className="evaluations-empty-text">Video link not provided.</p>
      )}
    </div>
  );
};

export const ProjectSlidesSection: React.FC<SectionProps> = ({ submission, competitionId }) => {
  const labels = projectLabels(competitionId);
  const artifacts = submission.manifest?.artifacts;
  const pdfArtifact = findArtifactByExtensions(artifacts, ['.pdf']);
  const pptArtifact = findArtifactByExtensions(artifacts, ['.ppt', '.pptx']);

  return (
    <div className="evaluations-multi-section">
      <h3>{labels.slidesHeading}</h3>
      {labels.showPptDownload && pptArtifact?.url && (
        <a href={pptArtifact.url} download target="_blank" rel="noopener noreferrer" className="evaluations-pdf-link">
          Download PowerPoint slides
        </a>
      )}
      {pdfArtifact?.url ? (
        <>
          <a
            href={pdfArtifact.url}
            target="_blank"
            rel="noopener noreferrer"
            className="evaluations-pdf-link"
          >
            📄 Open PDF in new tab
          </a>
          <div className="evaluations-pdf-wrapper evaluations-comp14-pdf-wrapper">
            <iframe
              src={pdfArtifact.url}
              title={`Competition ${competitionId} PDF`}
              className="evaluations-pdf-viewer"
            />
          </div>
        </>
      ) : (
        <p className="evaluations-empty-text">PDF artifact not found.</p>
      )}
    </div>
  );
};

export const ProjectDescriptionSection: React.FC<SectionProps> = ({ submission, competitionId }) => {
  const labels = projectLabels(competitionId);
  const manifest = submission.manifest;
  const briefText = getStringManifestFieldValue(manifest, 'brief_text')
    || getStringManifestFieldValue(manifest, 'brief_markdown');
  const markdownArtifact = findArtifactByExtensions(manifest?.artifacts, ['.md', '.markdown']);

  return (
    <div className="evaluations-multi-section">
      <h3>{labels.descriptionHeading}</h3>
      {briefText ? (
        <div className="evaluations-markdown-card">
          <ReactMarkdown>{briefText}</ReactMarkdown>
        </div>
      ) : markdownArtifact?.url ? (
        <a
          href={markdownArtifact.url}
          target="_blank"
          rel="noopener noreferrer"
          className="evaluations-pdf-link"
        >
          Open legacy markdown file
        </a>
      ) : (
        <p className="evaluations-empty-text">{labels.emptyDescription}</p>
      )}
    </div>
  );
};

export const ProjectSubmissionView: React.FC<SectionProps & { meta?: React.ReactNode }> = ({ submission, competitionId, meta }) => (
  <div className="evaluations-multi-card">
    {meta && <div className="evaluations-multi-meta">{meta}</div>}
    <ProjectOverviewSection submission={submission} competitionId={competitionId} />
    <ProjectVideoSection
      key={`${submission.submission_id}-${submission.submission_version}`}
      submission={submission}
      competitionId={competitionId}
    />
    <ProjectSlidesSection submission={submission} competitionId={competitionId} />
    <ProjectDescriptionSection submission={submission} competitionId={competitionId} />
  </div>
);
